<script lang="ts" setup>
  import type { PropType } from 'vue';

  defineOptions({
    inheritAttrs: false,
  });

  type File = {
    displayName: string | null;
    filename: string;
    name: string;
    description: string | null;
    updatedAt: string;
  }

  defineProps({
    header: { type: String },
    headerType: { type: String },
    content: { type: String },
    files: { type: Array as PropType<File[]>, required: true, default: [] },
  });
</script>
<template>
  <SfSection :headerType :title="header" class="download">
    <QuillContent v-if="content" :content />
    <div v-if="files.length > 0" class="download__files">
      <article v-for="file in files" class="file">
        <h3 class="file__file-name">
          {{ file.displayName || file.name }}
        </h3>
        <QuillContent v-if="file.description" :content="file.description" />
        <div class="file__footer">
          <SfButton :aria-label="$t('SHOP.COMMON.ARIA.DOWNLOAD_FILE') + ' ' + (file.displayName || file.name)" :href="$route('public.media.file.download', { filename: file.filename })" download rel="noreferrer noopener" small target="_blank">
            <template #default>
              {{ $t('SHOP.COMMON.ACTION.DOWNLOAD') }}
            </template>
            <template #after>
              <SfIconDownload aria-hidden="true" />
            </template>
          </SfButton>
          <p v-if="file.updatedAt">
            {{ $t('SHOP.COMMON.LABEL.LAST_MODIFICATION') }}
            <time>
              {{ file.updatedAt }}
            </time>
          </p>
        </div>
      </article>
    </div>
    <SfDataPlaceholder v-else />
  </SfSection>
</template>
<style lang="scss">
  @use '@storefront/styles/mixins/media' as media;

  :root {
    --sf-files-block-border-color: var(--secondary-border);
    --sf-files-block-border-radius: var(--border-radius);
    --sf-files-gap: 0.75rem;
    --sf-files-block-gap: 1rem;
    --sf-files-block-padding: 1rem;
    --sf-files-block-section-padding: 0;
    --sf-files-block-section-gap: 1.5rem;
    --sf-files-block-header-gap: 1rem;

    @include media.query(lg) {
      --sf-files-block-section-padding: 0 1rem;
      --sf-files-block-padding: 0.75rem;
      --sf-files-block-gap: 0.75rem;
      --sf-files-gap: 0.5rem;
      --sf-files-block-section-gap: 1rem;
      --sf-files-block-header-gap: 0.5rem;
    }
  }

  .download {
    display: flex;
    flex-direction: column;
    gap: var(--sf-files-block-section-gap);
    padding: var(--sf-files-block-section-padding);
    container-type: inline-size;

    &__header {
      display: flex;
      flex-direction: column;
      gap: var(--sf-files-block-header-gap);
    }

    &__file-name {
      font-weight: var(--font-weight-bold);
      word-break: break-all;
    }

    &__files {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: var(--sf-files-gap);

      @container (width < 767px) {
        grid-template-columns: 1fr;
        padding: 0 0.625rem;
      }

      .file {
        display: flex;
        flex: 1 1;
        gap: var(--sf-files-block-gap);
        padding: var(--sf-files-block-padding);
        border: 1px solid var(--sf-files-block-border-color);
        border-radius: var(--sf-files-block-border-radius);
        flex-direction: column;
        justify-content: space-between;

        .ql-editor {
          padding: 0;
        }

        h1, h2, h3, h4, h5, h6 {
          font-size: 1rem;
          line-height: 1.7;
        }

        p {
          font-size: 0.875rem;
        }

        &__footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 1rem;

          p {
            font-size: var(--font-size-sm);
            color: var(--color-secondary);
          }
        }
      }
    }
  }
</style>
